import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaArrowRight, FaChartLine, FaImage, FaNewspaper } from 'react-icons/fa'
import Navbar from '../components/Navbar'
import { axiosInstance } from '../context/AuthContext'
import { useAuthContext } from '../hooks/useAuthContext'

const ANALYTICS_ROLES = ['admin', 'curator', 'super_admin']

const shuffle = (items) => {
  const copy = [...items]
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1))
    ;[copy[index], copy[randomIndex]] = [copy[randomIndex], copy[index]]
  }
  return copy
}

const normalizeGalleryIds = (galleryId) => {
  if (Array.isArray(galleryId)) return galleryId.filter(Boolean)
  return galleryId ? [galleryId] : []
}

const formatDate = (value) => {
  if (!value) return ''
  return new Date(value).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const LoadingBlock = ({ className }) => (
  <div className={`animate-pulse rounded-xl bg-gray-200 ${className}`} />
)

const ArtworkCard = ({ artwork, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(artwork._id)}
    className="group overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
  >
    <div className="aspect-[4/3] overflow-hidden bg-gray-100">
      {artwork.imageBase64 ? (
        <img
          src={artwork.imageBase64}
          alt={artwork.title || 'Artwork'}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-gray-400"><FaImage size={26} /></div>
      )}
    </div>
    <div className="p-4">
      <p className="truncate text-base font-semibold text-gray-900">{artwork.title || 'Untitled artwork'}</p>
      <p className="mt-1 truncate text-xs uppercase tracking-[0.12em] text-gray-500">
        {artwork.gallery_display_name || artwork.gallery_name || 'Linked gallery'}
      </p>
    </div>
  </button>
)

const BlogCard = ({ blog, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(blog._id)}
    className="group flex min-h-[280px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
  >
    <div className="h-36 overflow-hidden bg-slate-100">
      {blog.featuredImage ? (
        <img src={blog.featuredImage} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
      ) : (
        <div className="flex h-full items-center justify-center text-slate-400"><FaNewspaper size={25} /></div>
      )}
    </div>
    <div className="flex flex-1 flex-col p-4">
      <p className="text-xs uppercase tracking-[0.12em] text-gray-500">{formatDate(blog.createdAt)}</p>
      <h3 className="mt-2 line-clamp-2 text-lg font-semibold text-gray-900">{blog.title || 'Untitled post'}</h3>
      <p className="mt-2 line-clamp-3 text-sm leading-6 text-gray-600">{blog.excerpt || 'Read the latest from Artization.'}</p>
    </div>
  </button>
)

const FeedPage = () => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const [artworks, setArtworks] = useState([])
  const [blogs, setBlogs] = useState([])
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [error, setError] = useState('')

  const permissions = user?.permissions || []
  const galleryIds = normalizeGalleryIds(user?.galleryId)
  const primaryGalleryId = galleryIds[0]
  const canViewAnalytics = ANALYTICS_ROLES.some((role) => permissions.includes(role)) && galleryIds.length > 0

  useEffect(() => {
    let isCurrent = true

    const loadFeed = async () => {
      setLoading(true)
      setError('')
      const [artworksResult, blogsResult] = await Promise.allSettled([
        axiosInstance.get('/artworks?view=gallery'),
        axiosInstance.get('/blog?page=1&limit=12&status=1'),
      ])

      if (!isCurrent) return

      if (artworksResult.status === 'fulfilled') {
        setArtworks(shuffle(artworksResult.value.data?.artworks || []).slice(0, 6))
      } else {
        setArtworks([])
      }

      if (blogsResult.status === 'fulfilled') {
        setBlogs(shuffle(blogsResult.value.data?.blogs || []).slice(0, 3))
      } else {
        setBlogs([])
      }

      if (artworksResult.status === 'rejected' && blogsResult.status === 'rejected') {
        setError('The feed could not be loaded right now.')
      }
      setLoading(false)
    }

    loadFeed()
    return () => { isCurrent = false }
  }, [user])

  useEffect(() => {
    let isCurrent = true

    const loadAnalytics = async () => {
      if (!canViewAnalytics) {
        setAnalytics(null)
        return
      }

      setAnalyticsLoading(true)
      try {
        const response = await axiosInstance.get(`/analytics/gallery/${encodeURIComponent(primaryGalleryId)}/emotions?limit=100`)
        if (isCurrent) setAnalytics(response.data)
      } catch (requestError) {
        if (isCurrent) setAnalytics(null)
      } finally {
        if (isCurrent) setAnalyticsLoading(false)
      }
    }

    loadAnalytics()
    return () => { isCurrent = false }
  }, [canViewAnalytics, primaryGalleryId])

  const emotionCounts = (analytics?.emotions || []).reduce((counts, item) => {
    const emotion = item.emotion || 'neutral'
    counts[emotion] = (counts[emotion] || 0) + 1
    return counts
  }, {})
  const totalSignals = Object.values(emotionCounts).reduce((sum, count) => sum + count, 0)

  return (
    <div className="min-h-screen bg-[#f5f7fa] text-gray-900">
      <Navbar />
      <main className="mx-auto max-w-7xl px-5 pb-24 pt-10 md:px-10 md:pl-28 md:pt-14">
        <section className="mb-10 flex flex-col justify-between gap-6 border-b border-gray-200 pb-8 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-600">Your Artization feed</p>
            <h1 className="mt-3 max-w-2xl text-4xl font-semibold tracking-tight text-gray-950 md:text-5xl">
              Welcome back, {user?.firstName || 'artist'}.
            </h1>
            <p className="mt-4 max-w-xl text-base leading-7 text-gray-600">
              A fresh view of work, ideas, and gallery signals connected to your account.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/gallery')}
            className="inline-flex items-center gap-3 self-start rounded-lg bg-gray-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 md:self-auto"
          >
            Open gallery <FaArrowRight size={12} />
          </button>
        </section>

        {error && <div className="mb-8 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <section aria-labelledby="artworks-heading">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Selected at random</p>
              <h2 id="artworks-heading" className="mt-1 text-2xl font-semibold">Artwork from your gallery network</h2>
            </div>
            <button type="button" onClick={() => navigate('/gallery')} className="hidden items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800 sm:flex">See all <FaArrowRight size={11} /></button>
          </div>
          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((item) => <LoadingBlock key={item} className="h-72" />)}
            </div>
          ) : artworks.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {artworks.map((artwork) => <ArtworkCard key={artwork._id} artwork={artwork} onOpen={(id) => navigate(`/artwork/${id}`)} />)}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-gray-500">No artwork is available for your linked gallery yet.</div>
          )}
        </section>

        <section className="mt-14" aria-labelledby="blogs-heading">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">From the journal</p>
              <h2 id="blogs-heading" className="mt-1 text-2xl font-semibold">Ideas worth a closer look</h2>
            </div>
            <button type="button" onClick={() => navigate('/blog')} className="hidden items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800 sm:flex">All blogs <FaArrowRight size={11} /></button>
          </div>
          {loading ? (
            <div className="grid gap-5 md:grid-cols-3">{[1, 2, 3].map((item) => <LoadingBlock key={item} className="h-72" />)}</div>
          ) : blogs.length > 0 ? (
            <div className="grid gap-5 md:grid-cols-3">
              {blogs.map((blog) => <BlogCard key={blog._id} blog={blog} onOpen={(id) => navigate(`/blog/${id}`)} />)}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-gray-500">No published blog posts are available yet.</div>
          )}
        </section>

        {canViewAnalytics && (
          <section className="mt-14" aria-labelledby="signals-heading">
            <div className="mb-4 flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Authorized gallery view</p>
                <h2 id="signals-heading" className="mt-1 text-2xl font-semibold">Audience signals</h2>
              </div>
              <FaChartLine className="text-blue-600" size={22} />
            </div>
            {analyticsLoading ? (
              <LoadingBlock className="h-40" />
            ) : analytics ? (
              <div className="grid gap-4 sm:grid-cols-4">
                <div className="rounded-xl bg-gray-950 p-5 text-white sm:col-span-2">
                  <p className="text-xs uppercase tracking-[0.16em] text-white/60">{analytics.config?.galleryName || 'Linked gallery'}</p>
                  <p className="mt-5 text-4xl font-semibold">{totalSignals}</p>
                  <p className="mt-1 text-sm text-white/65">recent audience signals</p>
                </div>
                {['positive', 'neutral'].map((emotion) => (
                  <div key={emotion} className="rounded-xl border border-gray-200 bg-white p-5">
                    <p className="text-xs uppercase tracking-[0.16em] text-gray-500">{emotion}</p>
                    <p className="mt-5 text-3xl font-semibold text-gray-900">{emotionCounts[emotion] || 0}</p>
                    <p className="mt-1 text-sm text-gray-500">signals recorded</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 bg-white px-6 py-8 text-sm text-gray-500">No gallery analytics are available for the linked gallery yet.</div>
            )}
          </section>
        )}
      </main>
    </div>
  )
}

export default FeedPage
